'use client';

import { IMAGE_PLACEHOLDER } from '@/lib/Constants';
import { formatNumberToPriceText } from '@/lib/utils';
import { HomePageBanner } from '@/models/HomePage';
import { Product } from '@/models/Product';
import { isFuture, parseISO } from 'date-fns';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import OptimizedBannerListCarousel from './OptimizedBannerListCarousel';
import SmartImage from './SmartImage';

function pickActiveBanners(banners?: HomePageBanner[]): HomePageBanner[] {
  if (!banners?.length) return [];
  const match = banners.filter(
    (banner) => banner?.badgeEndTime && isFuture(parseISO(banner.badgeEndTime)),
  );
  return match.length > 0 ? [match[0]] : banners;
}

export default function BannerlistWithProduct({
  banners,
  product,
  dir,
  isPriority = false,
}: {
  banners?: HomePageBanner[];
  product?: Product;
  dir: string;
  isPriority?: boolean;
}) {
  const [bannersFiltered, setBannersFiltered] = useState<HomePageBanner[]>(() =>
    pickActiveBanners(banners),
  );

  useEffect(() => {
    setBannersFiltered(pickActiveBanners(banners));
  }, [banners]);

  return (
    <div
      className="flex flex-col md:flex-row gap-4 max-w-7xl w-full mx-auto items-stretch"
      dir={dir}
    >
      <div className="md:flex-1 h-full hover:cursor-pointer md:rounded-2xl content-center">
        <OptimizedBannerListCarousel banners={bannersFiltered} isPriority={isPriority} />
      </div>

      <Link
        href={product?.url ?? ''}
        prefetch={false}
        className="hidden md:flex flex-shrink-0 mx-auto w-full md:w-[25%] flex-col hover:cursor-pointer rounded-2xl border border-hzc_beige_300 bg-white transform transition-transform duration-300 hover:scale-105"
      >
        <div className="relative aspect-square w-full rounded-2xl overflow-hidden">
          <SmartImage
            src={product?.imageUrl || IMAGE_PLACEHOLDER}
            alt={`${product?.title} - Hemen Sipariş Ver` || `ürün görseli - ${product?.id}`}
            fill
            imageType="product"
            priority={false}
            loading="lazy"
            placeholder="blur"
            blurDataURL={'/blur.webp'}
            className="object-contain"
          />

          {product?.badge?.imageUrl && (
            <SmartImage
              src={product.badge.imageUrl}
              alt={product.badge.title || ''}
              width={48}
              height={48}
              imageType="icon" // ✅ EKLE
              loading="lazy"
              className="absolute top-2 right-2 w-auto"
            />
          )}
        </div>

        <div className="mx-auto py-2 text-center">
          <div className="font-semibold text-sm text-hzc_purple_400">{product?.title}</div>

          {typeof product?.priceBeforeDiscount === 'number' &&
            product?.priceBeforeDiscount !== product?.price &&
            product?.priceBeforeDiscount > 0 && (
              <div className="font-bold text-base text-hzc_purple_300 relative w-fit mx-auto">
                <div className="absolute w-full h-0.5 bg-hzc_purple_500 rounded-full rotate-6 bottom-1/2"></div>
                {formatNumberToPriceText(product.priceBeforeDiscount)} TL
              </div>
            )}

          <div className="font-semibold text-lg text-hzc_purple_500">
            {formatNumberToPriceText(product?.price || 0)} TL
          </div>
        </div>
      </Link>
    </div>
  );
}
